class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.addEventListener('click', (event) => {
      const removeCouponButton = event.target.closest('.remove-discount-code');
      if (!removeCouponButton || removeCouponButton.disabled) return;
      event.preventDefault();
      this.removeCouponCode(removeCouponButton);
    });
  }

  removeCouponCode(button) {
    if (button) {
      button.disabled = true;
      button.classList.add('loading');
      const spinner = button.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    // Cart-level discount codes are cleared through the Ajax Cart API's
    // `discount` property on /cart/update.js. We don't need anything from
    // this response body, so a failure to read/parse it (e.g. a third-party
    // script rewriting the response) must not block refreshing the drawer —
    // the cart mutation itself has already gone through by this point.
    return fetch(routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: '' }),
    })
      .catch((error) => {
        console.warn("Discount-update response could not be read, refreshing cart anyway:", error.message);
      })
      .then(() => fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`))
      .then((response) => response.json())
      .then((response) => {
        this.renderContents({ sections: response });
      })
      .catch((error) => {
        console.error("Error refreshing cart after removing discount:", error.message);
        if (button) {
          button.disabled = false;
          button.classList.remove('loading');
          const spinner = button.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Only count a real open, not re-renders of an already open drawer
    // (discount apply/remove, quantity changes call open() again).
    if (!this.classList.contains('active')) this.publishViewCart();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => { this.classList.add('animate', 'active') });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
    if(document.querySelector("#Details-menu-drawer-container") && document.querySelector("#Details-menu-drawer-container").hasAttribute("open")){
      document.querySelector("#Details-menu-drawer-container").removeAttribute("open");
      document.querySelector("#Details-menu-drawer-container").classList.remove("menu-opening");
      document.querySelector(".header__icon--menu") && document.querySelector(".header__icon--menu").setAttribute("aria-expanded",false);
      document.body.classList.remove("overflow-hidden-tablet");
      document.querySelector(".section-header") && document.querySelector(".section-header").classList.remove("menu-open");
    }
    if(document.querySelector(".header__search details") && document.querySelector(".header__search details").hasAttribute("open")){
      document.querySelector(".header__search details").removeAttribute("open");
      document.querySelector(".section-header") && document.querySelector(".section-header").classList.remove("menu-open");
    }
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  // Shopify only fires the standard cart_viewed event on the /cart page, and
  // themes can't publish standard events. Publish a custom event instead; the
  // "GA4 funnel fixes" custom pixel (Settings > Customer events) sends it to GA4 as
  // view_cart.
  publishViewCart() {
    if (this.viewCartPending || !window.Shopify || !Shopify.analytics) return;
    this.viewCartPending = true;

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        Shopify.analytics.publish('anipet:view_cart', {
          currency: cart.currency,
          value: cart.total_price / 100,
          items: cart.items.map((item) => ({
            product_id: item.product_id,
            variant_id: item.variant_id,
            sku: item.sku,
            title: item.product_title,
            variant_title: item.variant_title,
            vendor: item.vendor,
            product_type: item.product_type,
            price: item.final_price / 100,
            quantity: item.quantity,
          })),
        });
      })
      .catch((error) => console.warn('view_cart tracking failed:', error.message))
      .finally(() => {
        this.viewCartPending = false;
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState,discount,discountcode) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      // console.log(parsedState.sections[section.id],"parsedState.sections[section.id]")
      if(discount){
        const parser = new DOMParser();
        let CARThtml = parser.parseFromString(parsedState.sections[section.id], "text/html");
        var discount_applied = false;
        if(CARThtml.querySelector("ul.discounts.list-unstyled")){
          CARThtml.querySelectorAll("ul.discounts.list-unstyled").forEach((ele) => {
            if(ele.innerHTML){
              discount_applied = true;
              return false;
            }
          })
          if(discount_applied){
            console.log("discount applied",discount_applied)
            setTimeout(function(){
              document.querySelector(".promo-code-wrap").classList.add("open");
              document.querySelector("cart-coupon-form .error").classList.add("d-none");
              }, 500);
          }else{
            setTimeout(function(){
              let pre_Val = discountcode;
              document.querySelector(".promo-code-wrap").classList.add("open");
              document.querySelector("cart-coupon-form .error").classList.remove("d-none");
              document.querySelector("cart-coupon-form input[type='text']").value = pre_Val;
            }, 500);
            
          }
        }
      }
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


//Coupon Code
class PromoCodeSection extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.target.classList.contains('cart-coupon-main') ? this.querySelector('.promo-code-wrap').classList.toggle('open') : null;
  
    });
  }
}

customElements.define('promo-code-wrap', PromoCodeSection);


class CartCouponCodeButton extends HTMLElement {
  constructor() {
    super();
    let current_form = this;
    this.querySelector('#coupon-form').addEventListener('submit', (event) => {

      event.preventDefault();
      this.submitForm(current_form.querySelector('.promo-input.couponCode').value)
    });
  }
 

   applyCouponCode(discountCode) {
    // Use the same Ajax Cart API `discount` property on /cart/update.js that
    // removal uses, instead of the old /discount/{code} checkout-session
    // link. Mixing the two meant a code applied via the checkout-session
    // link never got cleared by a cart-level removal, so checkout kept
    // showing the discount even after it was removed from the drawer.
    // As with removal, we don't depend on this response's body — success is
    // determined by re-checking cart.cart_level_discount_applications after
    // the section refresh in submitForm() below.
    return fetch(routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: discountCode }),
    }).catch((error) => {
      console.warn("Discount-update response could not be read, refreshing cart anyway:", error.message);
    });
  }
  
  submitForm(discountCode){
    let current_this = this
    const submitButton = this.querySelector('.btn-promo');
    const spinner = submitButton && submitButton.querySelector('.loading-overlay__spinner');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    }
    const resetButton = () => {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      }
    };
    current_this.applyCouponCode(discountCode)
    .then(() => {
        console.log("Discount applied successfully!");
        fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
        .then((response) => response.json())
        .then((response) => {
            let responseFormated = {
                sections: response
            }
            let cart_drawer = document.querySelector('cart-drawer');
            cart_drawer.renderContents(responseFormated,"discount",discountCode)
            resetButton();
        })
      })
    .catch(error => {
        console.error("Error applying discount:", error.message);
        resetButton();
    });
  }
}

customElements.define('cart-coupon-form', CartCouponCodeButton);




